import { FC, useMemo } from 'react';

import { useGetInventoryTenantId, useToken } from 'api';
import { useChangeStep, useUtilityWorkflow } from 'api/inventory/workflows';
import permissions from 'common/permissions';
import strings from 'common/strings';
import BaseStepDropdown from 'components/shared/BaseStepDropdown';
import { useGetProductServiceSettings, usePermissions } from 'hooks';
import { StepItem } from 'models';
import { recurseChildSteps } from 'utils/workflow';

interface StepDropdownProps {
  containerClassName?: string;
  loaderType?: 'srp' | 'default' | 'srpGradient';
  currentStep?: Pick<StepItem, 'id' | 'name' | 'sharedTenantId'>;
  inventoryId?: string;
  reconTenantId?: string;
}

const StepDropdown: FC<StepDropdownProps> = ({
  inventoryId,
  containerClassName,
  loaderType,
  currentStep,
  reconTenantId,
}) => {
  const { hasPermission } = usePermissions();
  const { getProductServiceSetting } = useGetProductServiceSettings();
  const { data: sessionData } = useToken();
  const inventoryTenantIdQuery = useGetInventoryTenantId(
    inventoryId,
    !!reconTenantId
  );
  const tenantId = inventoryTenantIdQuery.data?.data ?? reconTenantId;
  const { data: queryWorkflowData } = useUtilityWorkflow(tenantId);
  const changeStepMutation = useChangeStep(inventoryId, tenantId);

  const workflow = queryWorkflowData?.data;
  const currentUserId = sessionData?.user?.id;

  const flattenedSteps = useMemo(
    () => (workflow ? recurseChildSteps(workflow) : []),
    [workflow]
  );
  const currentStepDefinition = currentStep
    ? flattenedSteps.find((listStep) => listStep.id === currentStep.id)
    : undefined;

  const handleStepSelect = async (newSelectedStepId: string) => {
    await changeStepMutation.mutateAsync(newSelectedStepId);
  };

  const allowMoveStepCheckingRestrictToStepUserList =
    getProductServiceSetting('RESTRICT_MOVE_STEP_TO_STEP_USER_LIST') !== '1' ||
    currentUserId === undefined ||
    currentStepDefinition?.allowedUsers === undefined ||
    currentStepDefinition?.allowedUsers.length === 0 ||
    currentStepDefinition?.allowedUsers
      .map((user) => user.id)
      .includes(currentUserId);
  const allowMoveStep =
    hasPermission(permissions.RECON_VDP_WORKFLOW_STEP_UPDATE) &&
    allowMoveStepCheckingRestrictToStepUserList;

  if (allowMoveStep) {
    return (
      <BaseStepDropdown
        containerClassName={containerClassName}
        workflow={workflow}
        isLoading={changeStepMutation.isLoading}
        loaderType={loaderType}
        currentStep={currentStep}
        onStepChange={handleStepSelect}
      />
    );
  }

  return (
    <span className="stepName">{currentStep?.name || strings.EMPTY_VALUE}</span>
  );
};

export default StepDropdown;
